import React from "react";

import { colorsPalettes } from "@similarweb/styles";

import {
    PhraseFilterSingleHeader,
    Text,
    PhraseFilterContainer,
    PhraseFilterTableContentContainer,
    PhraseFilterCompareTableContentContainer,
    TrafficShareWithTooltipContainer,
    PhraseFilterSingleTableRowContainer,
    ProgressBarSingleContainer,
    PhraseFilterCompareHeader,
    PhraseFilterSingleTableRow,
    PhraseFilterCompareTableContent,
} from "pro-features/components/filtersPanel/src/filterPanelStyled";
import type { IPhraseFilterProps } from "pro-features/components/filtersPanel/src/PhraseFilterUtilities";
import {
    createTableHeader,
    filterBySearchValue,
    ProgressBarSingle,
    SearchInputWrapper,
    shareFilter,
} from "pro-features/components/filtersPanel/src/PhraseFilterUtilities";
import { TrafficShareWithTooltip } from "pro-features/components/TrafficShare/src/TrafficShareWithTooltip";
import { i18nFilter } from "app/filters/i18nFilter";
import { smallNumbersPercentageFilter } from "app/filters/ngFilters";

export const phraseFilterDataEndpoints = {
    DEFAULT: "api/keywordphrases/TopPhrases",
};

const TRAFFIC_SHARE_PROGRESS_BAR_TOOLTIP_DEFAULT_HEADER_KEY =
    "website.analysis.keywords.table.filters.phrases.progress.bar.tooltip.header";

const tableHeadersCompareDefaultKeys = {
    KEYWORD_PHRASE: "website.analysis.keywords.table.filters.phrases.header.keyword",
    TOTAL_SHARE: "website.analysis.keywords.table.filters.phrases.header.total.share",
    COMPETITIVE_TRAFFIC_SHARE: "website.analysis.keywords.table.filters.phrases.header.competitive",
};

const tableHeadersSingleDefaultKeys = {
    KEYWORD_PHRASE: "website.analysis.keywords.table.filters.phrases.header.keyword",
    TRAFFIC_SHARE: "website.analysis.keywords.table.filters.phrases.header.traffic.share",
};

const i18n = i18nFilter();

export const PhraseFilter = (props: IPhraseFilterProps) => {
    const {
        chosenItems,
        tableHeadersCompare = [
            i18n(tableHeadersCompareDefaultKeys.KEYWORD_PHRASE),
            i18n(tableHeadersCompareDefaultKeys.TOTAL_SHARE),
            i18n(tableHeadersCompareDefaultKeys.COMPETITIVE_TRAFFIC_SHARE),
        ],
        tableHeadersSingle = [
            i18n(tableHeadersSingleDefaultKeys.KEYWORD_PHRASE),
            i18n(tableHeadersSingleDefaultKeys.TRAFFIC_SHARE),
        ],
        trafficShareProgressBarTooltipHeader = i18n(TRAFFIC_SHARE_PROGRESS_BAR_TOOLTIP_DEFAULT_HEADER_KEY),
        ...other
    } = props;
    const isCompareMode = chosenItems.length > 1;
    const commonProps = {
        ...other,
        chosenItems,
        tableHeadersCompare,
        tableHeadersSingle,
        trafficShareProgressBarTooltipHeader,
    };

    return (
        <PhraseFilterContainer>
            {isCompareMode ? <PhraseFilterCompare {...commonProps} /> : <PhraseFilterSingle {...commonProps} />}
        </PhraseFilterContainer>
    );
};

const createTableRowCompare = (
    onClickCallback: FixmeType,
    chosenItemsColors: FixmeType,
    trafficShareProgressBarTooltipHeader: string,
) =>
    function (item: FixmeType, index: number) {
        const { searchTerm, share, siteOrigins, hideData } = item;
        const onClick = () => onClickCallback(item);
        const shareValuePercents = shareFilter(share);
        const PERCENTAGE_FILTER_SIGN = "%";
        const PERCENTAGE_FILTER_FRACTION_LENGTH = 2;
        const trafficDistribution = Object.keys(siteOrigins).map((site) => {
            const trafficShareValue = siteOrigins[site];
            const fixedTrafficShareValue = (trafficShareValue * 100).toFixed();
            return {
                name: site,
                width: trafficShareValue,
                text: fixedTrafficShareValue + PERCENTAGE_FILTER_SIGN,
                tooltipText: smallNumbersPercentageFilter()(trafficShareValue, PERCENTAGE_FILTER_FRACTION_LENGTH),
                color: colorsPalettes.carbon[0],
                backgroundColor: chosenItemsColors[site],
            };
        });
        const TRAFFIC_SHARE_CLASS_NAME = "traffic-share-progress-bar";
        const DEFAULT_FONT_SIZE = 14;
        return (
            <PhraseFilterCompareTableContentContainer
                key={index}
                onClick={onClick}
            >
                <PhraseFilterCompareTableContent>
                    <Text
                        fontSize={DEFAULT_FONT_SIZE}
                        fontWeight={item.fontWeight ? item.fontWeight : null}
                        fontStyle={item.fontStyle ? item.fontStyle : null}
                    >
                        {searchTerm}
                    </Text>
                    <Text
                        fontSize={DEFAULT_FONT_SIZE}
                        hideData={hideData}
                    >
                        {shareValuePercents}
                    </Text>
                    <TrafficShareWithTooltipContainer hideData={hideData}>
                        <TrafficShareWithTooltip
                            data={trafficDistribution}
                            title={trafficShareProgressBarTooltipHeader}
                            trafficShareClassName={TRAFFIC_SHARE_CLASS_NAME}
                        />
                    </TrafficShareWithTooltipContainer>
                </PhraseFilterCompareTableContent>
            </PhraseFilterCompareTableContentContainer>
        );
    };

const PhraseFilterCompare: React.FunctionComponent<IPhraseFilterProps> = (props) => {
    const {
        filterData,
        onClickCallback,
        chosenItems,
        searchPlaceholder,
        trafficShareProgressBarTooltipHeader,
        tableHeadersCompare,
    } = props;
    const chosenItemsColors = chosenItems.reduce(
        (tempObject, { name, color }) => ({ [name]: color, ...tempObject }),
        {},
    );
    const [searchInputValue, setSearchInputValue] = React.useState(String());

    const handleSearchInputValueChange = (value = "") => {
        setSearchInputValue(value.toLowerCase());
    };

    return (
        <>
            <SearchInputWrapper
                onChange={handleSearchInputValueChange}
                searchPlaceholder={searchPlaceholder}
            />
            <PhraseFilterCompareHeader>{tableHeadersCompare?.map(createTableHeader)}</PhraseFilterCompareHeader>
            <PhraseFilterTableContentContainer>
                {filterData.filter(filterBySearchValue(searchInputValue)).map(
                    // @ts-ignore FIXME: strict migration - Argument of type 'string | undefined' is not assignable to parameter of type 'string' — trafficShareProgressBarTooltipHeader always defaulted by parent PhraseFilter
                    createTableRowCompare(onClickCallback, chosenItemsColors, trafficShareProgressBarTooltipHeader),
                )}
            </PhraseFilterTableContentContainer>
        </>
    );
};

const createTableRowSingle = (onClickCallback: FixmeType) =>
    function (item: FixmeType, index: number) {
        const { searchTerm, share, hideData } = item;
        const onClick = () => onClickCallback(item);
        return (
            <PhraseFilterSingleTableRowContainer
                key={index}
                onClick={onClick}
            >
                <PhraseFilterSingleTableRow>
                    <Text
                        fontSize={14}
                        fontWeight={item.fontWeight ? item.fontWeight : null}
                        fontStyle={item.fontStyle ? item.fontStyle : null}
                    >
                        {searchTerm}
                    </Text>
                    <ProgressBarSingleContainer>
                        <ProgressBarSingle
                            share={share}
                            hideData={hideData}
                        />
                    </ProgressBarSingleContainer>
                </PhraseFilterSingleTableRow>
            </PhraseFilterSingleTableRowContainer>
        );
    };

const PhraseFilterSingle: React.FunctionComponent<IPhraseFilterProps> = (props) => {
    const { filterData, onClickCallback, searchPlaceholder, tableHeadersSingle } = props;
    const [searchInputValue, setSearchInputValue] = React.useState(String());

    const handleSearchInputValueChange = (value = "") => {
        setSearchInputValue(value.toLowerCase());
    };

    return (
        <>
            <SearchInputWrapper
                onChange={handleSearchInputValueChange}
                searchPlaceholder={searchPlaceholder}
            />
            <PhraseFilterSingleHeader>{tableHeadersSingle?.map(createTableHeader)}</PhraseFilterSingleHeader>
            <PhraseFilterTableContentContainer>
                {filterData
                    // if the user search for something we don't show the any option
                    .filter((term) => {
                        if (searchInputValue) {
                            return term.searchTerm !== "Any topic";
                        } else {
                            return term;
                        }
                    })
                    .filter(filterBySearchValue(searchInputValue))
                    .map(createTableRowSingle(onClickCallback))}
            </PhraseFilterTableContentContainer>
        </>
    );
};
